import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect, useCallback } from 'react';
import { HandHeart, Search, Mail, Phone, Send, Loader2, Trash2, Clock, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../App';

const STATUS = {
  new: ['New', 'bg-amber-100 text-amber-800'],
  contacted: ['Contacted', 'bg-blue-100 text-blue-800'],
  active: ['Active volunteer', 'bg-green-100 text-green-800'],
  declined: ['Declined', 'bg-slate-100 text-slate-700'],
  archived: ['Archived', 'bg-slate-100 text-slate-500'],
};
const DELIVERY = { sent: ['Sent', CheckCircle, 'text-green-700'], queued: ['Sending…', Clock, 'text-amber-700'], sending: ['Sending…', Clock, 'text-amber-700'], failed: ['Failed', AlertTriangle, 'text-red-700'], unknown: ['—', Clock, 'text-slate-500'] };
const ORG = 'Gujarat Samaj Nottingham';
const first = (name) => (name || '').trim().split(' ')[0] || 'there';
/** One-click reply templates. "Dear <name>" and the sign-off are added automatically by the email. */
const TEMPLATES = [
  { key: 'received', label: 'Thank you — we will come back to you', subject: `Thank you for offering to volunteer — ${ORG}`,
    body: (v) => `Thank you so much for offering your time to ${ORG} — it truly means a lot to our community.

We have received your application and one of our committee members will come back to you shortly to have a chat about how you would like to help${(v.interests || []).length ? ` (you mentioned ${v.interests.slice(0, 2).join(' and ')})` : ''}.

In the meantime, if you have any questions at all, please just reply to this email.

With warm thanks and kind regards.` },
  { key: 'call', label: 'Can we call you?', subject: `Volunteering with ${ORG} — a quick chat`,
    body: (v) => `Thank you for offering to volunteer with ${ORG}.

We would love to have a short, friendly chat to hear more about what you enjoy and when you are available. Could you let us know a good day and time to call you${v.phone ? ` on ${v.phone}` : ''}, or reply with a number that suits you best?

We look forward to speaking with you.

Kind regards.` },
  { key: 'invite', label: 'Invitation to meet the team', subject: `Come and meet the ${ORG} team`,
    body: () => `Thank you for offering to volunteer with ${ORG}.

We would be delighted to welcome you to our next committee/volunteer get-together at the Samaj hall so you can meet the team and see how everything works. Please could you let us know which of the following suits you best:

• [date and time option 1]
• [date and time option 2]

There is no obligation — it is simply a chance to say hello over a cup of chai.

Kind regards.` },
  { key: 'event', label: 'Help at an upcoming event', subject: `Would you be able to help at our upcoming event?`,
    body: () => `Thank you for offering to volunteer with ${ORG}.

We are preparing for [event name] on [date] and would be very grateful for an extra pair of hands with [e.g. welcoming guests, the food stall, setting up the hall]. Volunteers are asked to arrive around [time].

Please reply to let us know if you are able to join us — and do not worry if the date does not suit; there will be plenty more opportunities.

Kind regards.` },
  { key: 'later', label: 'Nothing right now — we will keep in touch', subject: `Thank you — we will be in touch when the next opportunity comes up`,
    body: () => `Thank you so much for offering to volunteer with ${ORG}.

At the moment we have all the help we need for our current activities, but we have kept your details safely and will get in touch as soon as the next opportunity comes up — usually around our festival season.

Thank you again for your generosity; it is people like you who keep our community strong.

Kind regards.` },
];

const fmt = (s) => s ? new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export function VolunteersAdminTab({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('open');   // open = everything except archived
  const [q, setQ] = useState('');
  const [current, setCurrent] = useState(null);   // full volunteer record
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [reply, setReply] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams(); if (filter !== 'open') params.set('status', filter); if (q.trim()) params.set('q', q.trim());
      setData(await api.getAuth(`/api/admin/volunteers?${params}`, token));
    } catch (e) { toast.error(e.message); setData({ volunteers: [], counts: {} }); }
  }, [token, filter, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = async (v) => {
    try {
      const d = await api.getAuth(`/api/admin/volunteers/${v.volunteer_id}`, token);
      setCurrent(d.volunteer); setNotes(d.volunteer.notes || '');
      setReply({ subject: TEMPLATES[0].subject, body: TEMPLATES[0].body(d.volunteer) });
    } catch (e) { toast.error(e.message); }
  };
  const refreshCurrent = async () => { if (!current) return; try { setCurrent((await api.getAuth(`/api/admin/volunteers/${current.volunteer_id}`, token)).volunteer); } catch { /* ignore */ } load(); };

  const setStatus = async (status) => { try { const d = await api.putAuth(`/api/admin/volunteers/${current.volunteer_id}`, { status }, token); setCurrent(d.volunteer); toast.success(`Marked as ${STATUS[status][0]}`); load(); } catch (e) { toast.error(e.message); } };
  const saveNotes = async () => { setSavingNotes(true); try { const d = await api.putAuth(`/api/admin/volunteers/${current.volunteer_id}`, { notes }, token); setCurrent(d.volunteer); toast.success('Notes saved'); } catch (e) { toast.error(e.message); } setSavingNotes(false); };
  const sendReply = async () => {
    if (!reply.subject.trim() || reply.body.trim().length < 5) { toast.error('Please write a subject and a message'); return; }
    setSending(true);
    try {
      const d = await api.postAuth(`/api/admin/volunteers/${current.volunteer_id}/reply`, { subject: reply.subject, body: reply.body, mark_contacted: true }, token);
      setCurrent(d.volunteer); toast.success('Reply is being sent from the Samaj mailbox'); setReply(r => ({ ...r, body: '' })); load();
      setTimeout(refreshCurrent, 6000);
    } catch (e) { toast.error(e.message); }
    setSending(false);
  };
  const doDelete = async () => { try { await api.deleteAuth(`/api/admin/volunteers/${current.volunteer_id}`, token); toast.success('Volunteer record deleted'); setConfirmDelete(false); setCurrent(null); load(); } catch (e) { toast.error(e.message); } };

  const counts = data?.counts || {};
  const list = data?.volunteers || [];

  return (
    <div className="space-y-5" data-testid="volunteers-admin-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Volunteers</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Sign-ups from the public Volunteer page. Reply by email straight from here — replies go out from the Samaj mailbox and the volunteer can answer you directly.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="volunteers-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      <div className="flex gap-2 flex-wrap items-center" data-testid="volunteers-filters">
        {[['open', 'Open'], ['new', 'New'], ['contacted', 'Contacted'], ['active', 'Active'], ['declined', 'Declined'], ['archived', 'Archived'], ['all', 'All']].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} className={filter === k ? 'bg-[hsl(var(--gs-navy))]' : ''} onClick={() => setFilter(k)} data-testid={`volunteers-filter-${k}`}>{l}{counts[k] != null ? ` (${counts[k]})` : ''}</Button>
        ))}
        <div className="relative ml-auto min-w-[220px]"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[hsl(var(--muted-foreground))]" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, interest…" className="pl-8 h-9" data-testid="volunteers-search" /></div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4">
        <Card>
          <CardContent className="p-0">
            {data === null ? <div className="p-4 space-y-2">{[0, 1, 2].map(i => <Skeleton key={i} className="h-14" />)}</div>
              : list.length === 0 ? <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="volunteers-empty"><HandHeart className="w-8 h-8 mx-auto mb-2" />No volunteer sign-ups {filter === 'open' ? 'waiting' : 'in this view'}.</div>
                : (
                  <ul className="divide-y max-h-[70vh] overflow-y-auto">
                    {list.map(v => (
                      <li key={v.volunteer_id}>
                        <button type="button" onClick={() => open(v)} className={`w-full text-left px-4 py-3 hover:bg-[hsl(var(--muted)/0.4)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--gs-navy))] ${current?.volunteer_id === v.volunteer_id ? 'bg-[hsl(var(--muted)/0.5)]' : ''}`} data-testid={`volunteer-row-${v.volunteer_id}`}>
                          <div className="flex items-center justify-between gap-2"><span className="font-medium truncate">{v.name}</span><Badge className={`text-xs shrink-0 ${STATUS[v.status]?.[1] || ''}`}>{STATUS[v.status]?.[0] || v.status}</Badge></div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{v.email}{v.phone ? ` · ${v.phone}` : ''}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] truncate mt-0.5">{(v.interests || []).join(', ') || '—'}</div>
                          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{fmt(v.created_at)}{v.reply_count ? ` · ${v.reply_count} repl${v.reply_count === 1 ? 'y' : 'ies'}` : ''}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
          </CardContent>
        </Card>

        <div>
          {!current ? <Card className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="volunteer-detail-empty">Select a volunteer to see details and reply.</Card> : (
            <Card data-testid="volunteer-detail">
              <CardContent className="p-5 space-y-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xl font-semibold" data-testid="volunteer-detail-name">{current.name}</div>
                    <div className="text-sm flex flex-wrap gap-3 mt-1">
                      <a href={`mailto:${current.email}`} className="flex items-center gap-1 underline" data-testid="volunteer-detail-email"><Mail className="w-3.5 h-3.5" /> {current.email}</a>
                      {current.phone && <a href={`tel:${current.phone}`} className="flex items-center gap-1 underline"><Phone className="w-3.5 h-3.5" /> {current.phone}</a>}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Applied {fmt(current.created_at)}{current.last_contacted_at ? ` · last contacted ${fmt(current.last_contacted_at)}` : ''}</div>
                  </div>
                  <div className="w-44">
                    <Label className="text-xs">Status</Label>
                    <Select value={current.status} onValueChange={setStatus}><SelectTrigger className="h-9" data-testid="volunteer-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border p-3"><div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Areas of interest</div><div className="flex flex-wrap gap-1">{(current.interests || []).length ? current.interests.map(i => <Badge key={i} variant="outline" className="text-xs">{i}</Badge>) : '—'}</div></div>
                  <div className="rounded-lg border p-3"><div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Availability</div>{current.availability || '—'}</div>
                  <div className="rounded-lg border p-3 sm:col-span-2"><div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Their message</div><div className="whitespace-pre-line" data-testid="volunteer-detail-message">{current.message || '—'}</div></div>
                </div>

                <div>
                  <Label htmlFor="vol-notes" className="text-xs">Private committee notes (never sent to the volunteer)</Label>
                  <Textarea id="vol-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. spoke on phone, available Saturdays, good for Navratri stall" data-testid="volunteer-notes" />
                  <Button size="sm" variant="outline" className="mt-2" onClick={saveNotes} disabled={savingNotes || notes === (current.notes || '')} data-testid="volunteer-notes-save">{savingNotes ? 'Saving…' : 'Save notes'}</Button>
                </div>

                <div className="rounded-lg border border-[hsl(var(--gs-navy)/0.3)] p-4 space-y-2 bg-[hsl(var(--muted)/0.2)]" data-testid="volunteer-reply-box">
                  <div className="font-medium flex items-center gap-2"><Send className="w-4 h-4" /> Reply by email</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="volunteer-reply-templates">
                    <span className="text-xs text-[hsl(var(--muted-foreground))] self-center mr-1">Quick replies:</span>
                    {TEMPLATES.map(t => (
                      <Button key={t.key} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReply({ subject: t.subject, body: t.body(current) })} data-testid={`volunteer-template-${t.key}`}>{t.label}</Button>
                    ))}
                  </div>
                  <div><Label htmlFor="vol-subj" className="text-xs">Subject</Label><Input id="vol-subj" value={reply.subject} onChange={e => setReply({ ...reply, subject: e.target.value })} data-testid="volunteer-reply-subject" /></div>
                  <div><Label htmlFor="vol-body" className="text-xs">Message</Label><Textarea id="vol-body" rows={6} value={reply.body} onChange={e => setReply({ ...reply, body: e.target.value })} placeholder={`Dear ${first(current.name)}, …`} data-testid="volunteer-reply-body" /></div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Sent from the Samaj mailbox with the Samaj header; “Dear {current.name}” and a sign-off with your name are added automatically. The volunteer can reply directly to the email.</p>
                  <Button onClick={sendReply} disabled={sending} className="bg-[hsl(var(--gs-navy))]" data-testid="volunteer-reply-send">{sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send reply</Button>
                </div>

                {(current.replies || []).length > 0 && (
                  <div data-testid="volunteer-reply-history">
                    <div className="text-sm font-medium mb-2">Previous replies</div>
                    <ul className="space-y-2">
                      {[...current.replies].reverse().map((r, i) => { const D = DELIVERY[r.delivery] || DELIVERY.unknown; const Icon = D[1]; return (
                        <li key={stableRecordKey(r)} className="rounded-lg border p-3 text-sm">
                          <div className="flex items-center justify-between gap-2 flex-wrap"><span className="font-medium">{r.subject}</span><span className={`text-xs flex items-center gap-1 ${D[2]}`} data-testid="volunteer-reply-delivery"><Icon className="w-3.5 h-3.5" /> {D[0]}{r.sent_at ? ` ${fmt(r.sent_at)}` : ''}</span></div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">{fmt(r.at)} · {r.by}</div>
                          <div className="mt-1 whitespace-pre-line text-[hsl(var(--foreground)/0.85)]">{r.body}</div>
                          {r.error && <div className="text-xs text-red-700 mt-1">{r.error}</div>}
                        </li>); })}
                    </ul>
                  </div>
                )}

                <div className="pt-2 border-t flex justify-end">
                  <Button variant="ghost" size="sm" className="text-[hsl(var(--destructive))]" onClick={() => setConfirmDelete(true)} data-testid="volunteer-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete record</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="volunteer-delete-dialog">
          <DialogHeader><DialogTitle>Delete {current?.name}'s record?</DialogTitle><DialogDescription>This permanently removes their details and reply history (use this for data-removal requests). To simply tidy the list, choose the “Archived” status instead.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button><Button variant="destructive" onClick={doDelete} data-testid="volunteer-delete-confirm">Delete permanently</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
